/* eslint-disable react/no-unstable-nested-components */
import {colors} from "@c10h/colors";
import {debounce} from "lodash";
import {useTranslation} from "@i18n/client";
import React, {useEffect, useState} from "react";
import {Controller, Scene} from "react-scrollmagic";
import slugify from "slugify";
import {dev} from "src/components/_common/_constants";
import {useWindowSize} from "src/components/_common/Carbon";
import useMediaQuery from "src/useMediaQuery";

import Fade from "../../animations/Fade";
import NextImage from "../NextImage";
import {useIsMobile} from "src/utils/isMobile";

const CoreValues: React.VoidFunctionComponent = () => {
  const i18n = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const {height = 1000} = useWindowSize();
  const [componentHeight, setComponentHeight] = useState(1000);

  const isMobile = useIsMobile();

  const data = [
    {
      bgColor: colors.lightGreen,
      header: i18n.t("We assume karma exists"),
      text: i18n.t(
        "Assume that doing the right things consistently will yield disproportionate rewards, even if you can’t always tell how that will happen.",
      ),
      image: "/illustrations/man-planting-tree_",
      alt: i18n.t("Illustration of a person kneeling and planting a tree seedling outside"),
    },
    {
      bgColor: colors.lightBlue,
      header: i18n.t("We respect the craft"),
      text: i18n.t(
        "Value the collective wisdom that’s been accumulated by people who have been doing a particular job for a long time.",
      ),
      image: "/illustrations/people-watering-tree",
      alt: i18n.t("Illustration of three people working together to care for a young tree"),
    },
    {
      bgColor: colors.lightYellow,
      header: i18n.t("We put ourselves in\npositions of insight"),
      text: i18n.t(
        "Unique insight, which often comes from a position of direct observation, is the reason the Davids of the world can compete with the Goliaths.",
      ),
      image: "/illustrations/woman-sitting-by-tree",
      alt: i18n.t("Illustration of a person sitting and thinking beneath the shade of a tree"),
    },
    {
      bgColor: colors.lightPurple,
      header: i18n.t("We hire people who genuinely care"),
      text: i18n.t(
        "There’s no replacement for having people who care deeply about solving the problem we’re hiring them to solve.",
      ),
      image: "/illustrations/people-standing-under-tree",
      heightFactor: 1.3,
      alt: i18n.t(
        "Illustration of a large group of people gardening together beneath a large tree",
      ),
    },
  ];

  const activeItem = data[activeIndex];

  const toggleActiveIndex = (i: number) =>
    debounce(() => {
      setActiveIndex(i);
    }, 150);

  useEffect(() => {
    setComponentHeight(5000 - height);
  }, [height]);

  const isSm = useMediaQuery("sm");

  // @ts-expect-error TS7031, TS7031: Binding element 'item' implicitly has an 'any' type.,  Binding element 'i' implicitly has an 'any' type.
  const Item = ({item, i, isActive = true}) => (
    <div
      key={item.header}
      className={isActive ? "minh48" : "dn minh48"}
      onMouseEnter={toggleActiveIndex(i)}
    >
      <h3 className="t-200 mb4 fs28-sm tal-xs">{item.header}</h3>
      <p className="t-200 fs20 fs18-sm maxw40 tal-xs">{item.text}</p>
    </div>
  );

  return isSm ? (
    <div id={slugify(data[0].header)} className="mt20 bg-lightGreen">
      <h4 className="fs48 font-csb ph8 pt14 lh12">{i18n.t("Our core values")}</h4>
      <div className="m0 p0 df fdc">
        {data.map((item, i) => (
          <div key={item.header} className="ph8 pv10" style={{backgroundColor: item.bgColor}}>
            <div className="df mr20 ma mt3 mb3">
              {data.map((item, innerI) => (
                <a
                  key={item.bgColor}
                  href={`#${slugify(item.header)}`}
                  className="mr7 mb5 cp font-ib focus-bsDarkBlue3"
                  onClick={toggleActiveIndex(innerI)}
                  style={{
                    color: i === innerI ? colors.gray800 : colors.gray800 + 60,
                  }}
                >
                  0{innerI + 1}
                </a>
              ))}
            </div>
            <Item i={i} item={item} />
            <NextImage
              className="obfc w80 mt14"
              src={item.image}
              alt={item.alt}
              height={200 * (item.heightFactor || 1)}
              width={200}
            />
            <span id={slugify(data[i + 1]?.header || "")} />
          </div>
        ))}
      </div>
    </div>
  ) : (
    <Controller>
      <Scene
        enabled={!isMobile}
        triggerHook={0.4}
        indicators={dev}
        offset={20}
        duration={componentHeight + 600}
      >
        {/* @ts-expect-error TS7006, TS7006: Parameter 'progress' implicitly has an 'any' type.,  Parameter 'event' implicitly has an 'any' type. */}
        {(progress, event) => {
          if (event.state === "DURING") {
            if (progress < 0.25) {
              setActiveIndex(0);
            } else if (progress < 0.5) {
              setActiveIndex(1);
            } else if (progress < 0.75) {
              setActiveIndex(2);
            } else {
              setActiveIndex(3);
            }
          }

          return (
            <div
              className="t-1000 pt48 pb100 ph8"
              style={{
                backgroundColor: activeItem.bgColor,
              }}
            >
              <div className="maxw80 ma pos-r df" style={{height: componentHeight}}>
                <div className="fx zIndex0">
                  <div className="pos-sticky" style={{top: 500}}>
                    <h2 className="fs60 font-cb ml24 mb30 dn-lg wsnw">
                      {i18n.t("Our Core Values")}
                    </h2>
                    <div className="df aic">
                      <div className="df fdc mr20 mr6-md ma mt3">
                        {data.map((item, i) => (
                          <span
                            key={item.bgColor}
                            className="mb5 font-ib"
                            style={{
                              color: i === activeIndex ? colors.gray800 : colors.gray800 + 60,
                            }}
                          >
                            0{i + 1}
                          </span>
                        ))}
                      </div>
                      <div>
                        {data.map((item, i) => (
                          <Fade key={item.header} in={i === activeIndex}>
                            <Item item={item} i={i} isActive={i === activeIndex} />
                          </Fade>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="fx pos-r df fdc aife jcsb ma mt20" style={{height: "102%"}}>
                  {data.map((opt, i) => (
                    <Fade key={opt.image} in={i === activeIndex}>
                      <NextImage
                        className="obfc w80-md"
                        src={opt.image}
                        height={400 * (opt.heightFactor || 1)}
                        width={400}
                        alt={opt.alt}
                      />
                    </Fade>
                  ))}
                </div>
              </div>
            </div>
          );
        }}
      </Scene>
    </Controller>
  );
};

export default CoreValues;
